import React, { useId } from 'react';
import type { ActivityViewMode } from '../../core/types/activityViewMode.types';
import type { BalanzaEquationSide, ExploraSkin } from '../../core/types/payloads/explora.types';

export interface BalanzaSvgProps {
  currentValue: number;
  targetValue: number;
  difference: number;
  unitLabel?: string;
  displayEquation?: string;
  blockLabels?: string[];
  balanced?: boolean;
  size?: 'md' | 'lg';
  mode?: ActivityViewMode;
  skin?: ExploraSkin;
  fill?: boolean;
  onTargetChange?: (value: number) => void;
  onCurrentPreview?: (value: number) => void;
  /** Modo despeje: platillos con cajas ? y pesas */
  inverseMode?: boolean;
  equationLeft?: BalanzaEquationSide;
  equationRight?: BalanzaEquationSide;
  /** Cambia tras cada operación — dispara animación */
  animationGeneration?: number;
}

const VB = { w: 480, h: 320 };
const CX = 240;
const CY = 132;
const BEAM_HALF = 148;
const CHAIN = 32;
const BASE_Y = 288;

function endpoint(angleRad: number, side: -1 | 1) {
  return {
    x: CX + side * BEAM_HALF * Math.cos(angleRad),
    y: CY + side * BEAM_HALF * Math.sin(angleRad),
  };
}

function skinPalette(skin: ExploraSkin, balanced: boolean) {
  if (skin === 'algebra') {
    return {
      accent: balanced ? '#34d399' : '#a78bfa',
      accentSoft: balanced ? '#34d39933' : '#a78bfa33',
      left: balanced ? '#6ee7b7' : '#c4b5fd',
      right: '#818cf8',
      beam: balanced ? '#34d399' : '#9333ea',
      chip: 'linear-gradient(145deg, #c4b5fd, #818cf8)',
    };
  }
  if (skin === 'economy') {
    return {
      accent: balanced ? '#34d399' : '#38bdf8',
      accentSoft: balanced ? '#34d39933' : '#38bdf833',
      left: balanced ? '#6ee7b7' : '#7dd3fc',
      right: '#fcd34d',
      beam: balanced ? '#34d399' : '#0ea5e9',
      chip: 'linear-gradient(145deg, #38bdf8, #6366f1)',
    };
  }
  return {
    accent: balanced ? '#34d399' : '#fb7185',
    accentSoft: balanced ? '#34d39933' : '#fb718533',
    left: balanced ? '#6ee7b7' : '#fda4af',
    right: '#93c5fd',
    beam: balanced ? '#34d399' : '#64748b',
    chip: 'linear-gradient(145deg, #78716c, #44403c)',
  };
}

function Pan({
  x,
  y,
  angleRad,
  side,
  palette,
  children,
}: {
  x: number;
  y: number;
  angleRad: number;
  side: -1 | 1;
  palette: ReturnType<typeof skinPalette>;
  children: React.ReactNode;
}) {
  const panGrad = useId();
  const tilt = (-angleRad * 180) / Math.PI;
  const stroke = side === -1 ? palette.left : palette.right;

  return (
    <g transform={`translate(${x} ${y}) rotate(${tilt})`}>
      {/* Cadena */}
      {[0, 1, 2].map((i) => (
        <ellipse
          key={i}
          cx={0}
          cy={6 + i * 9}
          rx={2.5}
          ry={4}
          fill="none"
          stroke="color-mix(in srgb, var(--plugin-muted) 70%, transparent)"
          strokeWidth={1.5}
        />
      ))}
      <line
        x1={0}
        y1={0}
        x2={0}
        y2={CHAIN}
        stroke="color-mix(in srgb, var(--plugin-muted) 55%, transparent)"
        strokeWidth={1.5}
        strokeDasharray="3 2"
      />
      <g transform={`translate(0 ${CHAIN})`}>
        <defs>
          <linearGradient id={panGrad} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={stroke} stopOpacity={0.35} />
            <stop offset="100%" stopColor={stroke} stopOpacity={0.08} />
          </linearGradient>
        </defs>
        {/* Platillo */}
        <ellipse cx={0} cy={4} rx={54} ry={8} fill={`url(#${panGrad})`} stroke={stroke} strokeWidth={1.5} opacity={0.9} />
        <path
          d="M -52 0 Q -52 28 0 34 Q 52 28 52 0 Z"
          fill={`url(#${panGrad})`}
          stroke={stroke}
          strokeWidth={2}
          filter="drop-shadow(0 4px 8px rgba(0,0,0,0.15))"
        />
        <ellipse cx={0} cy={2} rx={46} ry={5} fill="none" stroke={stroke} strokeWidth={0.75} opacity={0.4} />
        <foreignObject x={-50} y={-48} width={100} height={44}>
          <div
            xmlns="http://www.w3.org/1999/xhtml"
            className="flex h-full flex-wrap items-end justify-center gap-0.5 px-1"
          >
            {children}
          </div>
        </foreignObject>
        <text
          x={0}
          y={48}
          textAnchor="middle"
          fill="var(--plugin-muted)"
          fontSize={9}
          fontWeight={600}
          letterSpacing="0.14em"
          opacity={0.75}
        >
          {side === -1 ? 'IZQUIERDA' : 'DERECHA'}
        </text>
      </g>
    </g>
  );
}

function BlockChip({ label, chipGrad }: { label: string; chipGrad: string }) {
  return (
    <span
      className="inline-block rounded-md px-1.5 py-0.5 font-display text-[9px] font-bold leading-tight shadow-sm"
      style={{ background: chipGrad, color: '#fff' }}
    >
      {label}
    </span>
  );
}

function MysteryBox() {
  return (
    <span className="balanza-mystery-box inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md border-2 border-violet-400/80 bg-violet-950/50 font-display text-[11px] font-extrabold text-violet-100 shadow-sm">
      ?
    </span>
  );
}

function WeightUnit({ unitLabel = 'kg' }: { unitLabel?: string }) {
  return (
    <span className="balanza-weight-unit inline-flex h-5 min-w-[1.25rem] shrink-0 items-center justify-center rounded-full bg-amber-600 px-1 font-display text-[8px] font-bold text-white shadow-sm">
      1{unitLabel ? unitLabel.slice(0, 2) : ''}
    </span>
  );
}

function EquationPanContents({
  side,
  unitLabel,
  chipGrad,
  animationGeneration,
}: {
  side: BalanzaEquationSide;
  unitLabel?: string;
  chipGrad: string;
  animationGeneration?: number;
}) {
  const maxVisible = 8;
  const boxes = Array.from({ length: Math.min(side.xBoxes, maxVisible) });
  const weights = Array.from({ length: Math.min(Math.max(0, side.constants), maxVisible) });
  const hiddenBoxes = Math.max(0, side.xBoxes - maxVisible);
  const hiddenWeights = Math.max(0, side.constants - maxVisible);

  return (
    <div
      key={animationGeneration ?? 0}
      className="balanza-pan-contents flex h-full flex-wrap items-end justify-center gap-0.5 px-0.5"
    >
      {boxes.map((_, i) => (
        <MysteryBox key={`x-${i}`} />
      ))}
      {hiddenBoxes > 0 && <BlockChip label={`+${hiddenBoxes}?`} chipGrad={chipGrad} />}
      {weights.map((_, i) => (
        <WeightUnit key={`w-${i}`} unitLabel={unitLabel} />
      ))}
      {hiddenWeights > 0 && <BlockChip label={`+${hiddenWeights}`} chipGrad={chipGrad} />}
      {side.xBoxes === 0 && side.constants === 0 && (
        <span className="text-[9px] font-semibold opacity-40">∅</span>
      )}
    </div>
  );
}

function EditableValue({
  value,
  unitLabel,
  onChange,
  className,
}: {
  value: number;
  unitLabel?: string;
  onChange?: (n: number) => void;
  className?: string;
}) {
  if (!onChange) {
    return (
      <span className={`font-display font-extrabold tabular-nums tracking-tight ${className ?? ''}`}>
        {value}
        {unitLabel ? <span className="ml-0.5 text-[9px] font-semibold opacity-60">{unitLabel}</span> : null}
      </span>
    );
  }
  return (
    <input
      type="number"
      className={`canvas-inline-input w-full bg-transparent text-center font-display font-extrabold tabular-nums ${className ?? ''}`}
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      onClick={(e) => e.stopPropagation()}
    />
  );
}

export function BalanzaSvgViewport({
  currentValue,
  targetValue,
  difference,
  unitLabel = 'kg',
  displayEquation,
  blockLabels = [],
  balanced,
  size = 'md',
  mode = 'projector',
  skin = 'physics',
  fill = false,
  onTargetChange,
  inverseMode = false,
  equationLeft,
  equationRight,
  animationGeneration,
}: BalanzaSvgProps) {
  const beamGrad = useId();
  const baseGrad = useId();
  const glowGrad = useId();
  const isBalanced = inverseMode ? true : (balanced ?? difference === 0);
  const tiltDeg = inverseMode ? 0 : Math.max(-14, Math.min(14, difference * 2.2));
  const angleRad = (tiltDeg * Math.PI) / 180;
  const palette = skinPalette(skin, isBalanced);
  const left = endpoint(angleRad, -1);
  const right = endpoint(angleRad, 1);
  const visibleBlocks = blockLabels.slice(-5);
  const isEditor = mode === 'editor';
  const editorTarget = isEditor ? onTargetChange : undefined;
  const valueSize = size === 'lg' ? 'text-base sm:text-lg' : 'text-sm';

  return (
    <div
      className={`explora-balanza-viewport relative w-full ${fill ? 'h-full min-h-0' : size === 'lg' ? 'max-w-4xl mx-auto' : 'max-w-2xl mx-auto'}`}
      style={fill ? undefined : { minHeight: size === 'lg' ? 200 : 160 }}
    >
      <svg
        viewBox={`0 0 ${VB.w} ${VB.h}`}
        preserveAspectRatio="xMidYMid meet"
        className={fill ? 'h-full w-full' : 'block h-auto w-full'}
        role="img"
        aria-label="Balanza interactiva"
      >
        <defs>
          <radialGradient id={glowGrad} cx="50%" cy="40%" r="55%">
            <stop offset="0%" stopColor={palette.accent} stopOpacity={0.12} />
            <stop offset="100%" stopColor={palette.accent} stopOpacity={0} />
          </radialGradient>
          <linearGradient id={beamGrad} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={palette.beam} stopOpacity={0.85} />
            <stop offset="50%" stopColor={palette.accent} />
            <stop offset="100%" stopColor={palette.beam} stopOpacity={0.85} />
          </linearGradient>
          <linearGradient id={baseGrad} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="var(--plugin-muted)" stopOpacity={0.5} />
            <stop offset="100%" stopColor="var(--plugin-muted)" stopOpacity={0.85} />
          </linearGradient>
        </defs>

        {/* Ambiente */}
        <rect x={0} y={0} width={VB.w} height={VB.h} fill={`url(#${glowGrad})`} />
        <line x1={48} y1={BASE_Y + 4} x2={VB.w - 48} y2={BASE_Y + 4} stroke="var(--plugin-border)" strokeWidth={1} opacity={0.35} />

        {/* Base */}
        <path
          d={`M ${CX - 36} ${BASE_Y} L ${CX} ${VB.h - 8} L ${CX + 36} ${BASE_Y} Z`}
          fill={`url(#${baseGrad})`}
          opacity={0.9}
        />
        <rect x={CX - 3} y={CY + 4} width={6} height={BASE_Y - CY - 4} rx={3} fill="var(--plugin-muted)" opacity={0.45} />

        {/* Fulcro */}
        <circle cx={CX} cy={CY} r={10} fill={palette.accentSoft} stroke={palette.accent} strokeWidth={2} />
        <circle cx={CX} cy={CY} r={4} fill={palette.accent} />

        {/* Brazo */}
        <g transform={`rotate(${tiltDeg} ${CX} ${CY})`}>
          <line
            x1={CX - BEAM_HALF}
            y1={CY}
            x2={CX + BEAM_HALF}
            y2={CY}
            stroke={`url(#${beamGrad})`}
            strokeWidth={6}
            strokeLinecap="round"
            filter="drop-shadow(0 2px 4px rgba(0,0,0,0.2))"
          />
          <circle cx={CX - BEAM_HALF} cy={CY} r={5} fill={palette.left} opacity={0.9} />
          <circle cx={CX + BEAM_HALF} cy={CY} r={5} fill={palette.right} opacity={0.9} />
        </g>

        <Pan x={left.x} y={left.y} angleRad={angleRad} side={-1} palette={palette}>
          {inverseMode && equationLeft ? (
            <EquationPanContents
              side={equationLeft}
              unitLabel={unitLabel}
              chipGrad={palette.chip}
              animationGeneration={animationGeneration}
            />
          ) : visibleBlocks.length > 0 ? (
            visibleBlocks.map((label, i) => <BlockChip key={`${label}-${i}`} label={label} chipGrad={palette.chip} />)
          ) : (
            <EditableValue
              value={currentValue}
              unitLabel={displayEquation ? undefined : unitLabel}
              className={`${valueSize} text-[var(--plugin-text)]`}
            />
          )}
        </Pan>

        <Pan x={right.x} y={right.y} angleRad={angleRad} side={1} palette={palette}>
          {inverseMode && equationRight ? (
            <EquationPanContents
              side={equationRight}
              unitLabel={unitLabel}
              chipGrad={palette.chip}
              animationGeneration={animationGeneration}
            />
          ) : (
            <EditableValue
              value={targetValue}
              unitLabel={displayEquation ? undefined : unitLabel}
              onChange={editorTarget}
              className={`${valueSize} text-[var(--plugin-text)] ${isEditor ? 'canvas-inline-input--active' : ''}`}
            />
          )}
        </Pan>

        {isBalanced && (
          <text x={CX} y={28} textAnchor="middle" fill="#34d399" fontSize={11} fontWeight={700} letterSpacing="0.12em">
            {inverseMode ? 'EQUILIBRIO ✓' : 'EQUILIBRIO'}
          </text>
        )}
      </svg>

      {displayEquation && (
        <div className="pointer-events-none absolute inset-x-0 top-2 text-center">
          <span
            className="inline-block rounded-full px-4 py-1 font-display text-xs font-semibold tracking-wide backdrop-blur-sm"
            style={{
              background: 'color-mix(in srgb, var(--plugin-bg) 80%, transparent)',
              color: 'var(--plugin-text)',
              border: '1px solid var(--plugin-border)',
              boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
            }}
          >
            {displayEquation}
          </span>
        </div>
      )}
    </div>
  );
}
